import FadeIn from '@/components/FadeIn';
import { useConsultation } from '@/components/ConsultationModal';
import { IMAGES, BUSINESS } from '@/lib/constants';
import { Shield, Users, Target, Clock, Award, Briefcase } from 'lucide-react';

const values = [
  { icon: Shield, title: 'Integrity', text: 'Transparent processes, honest communication, and absolute reliability in everything we do.' },
  { icon: Target, title: 'Precision', text: 'Every transaction categorized, every report verified, every detail accounted for with meticulous care.' },
  { icon: Users, title: 'Personal Service', text: 'Direct access to William Covington. No call centers, no outsourced support, no impersonal service.' },
  { icon: Clock, title: 'Responsiveness', text: 'Timely communication and proactive updates keep you informed and in control of your business.' },
  { icon: Award, title: 'Professionalism', text: 'Executive-level standards applied to every engagement, regardless of business size.' },
  { icon: Briefcase, title: 'Practical Expertise', text: 'Real-world business acumen combined with professional accounting knowledge to deliver meaningful support.' },
];

const metrics = [
  { number: '15+', label: 'Years of Experience' },
  { number: '50+', label: 'Businesses Supported' },
  { number: '12', label: 'Industries Served' },
];

export default function AboutPage() {
  const { setOpen } = useConsultation();

  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="bg-wc-charcoal py-24 lg:py-32" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <FadeIn>
            <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-4">About the Firm</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-white max-w-3xl leading-[1.1]">
              Professional Financial Support Built on Trust
            </h1>
            <p className="font-body text-base lg:text-lg text-white/55 mt-6 max-w-2xl leading-relaxed">
              William Covington & Associates is a premier bookkeeping, accounting support, and business consulting firm dedicated to helping small businesses operate with confidence.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* WILLIAM COVINGTON */}
      <section className="py-24 lg:py-32 bg-white" data-testid="about-william">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <FadeIn>
              <div className="sticky top-28">
                <img
                  src={IMAGES.williamCasual}
                  alt="William K. Covington"
                  className="w-full aspect-[3/4] object-cover object-top"
                  loading="lazy"
                />
                <div className="mt-6">
                  <p className="font-heading text-xl text-wc-black">William K. Covington</p>
                  <p className="font-body text-sm text-wc-muted mt-1">President & CEO</p>
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={150}>
              <div>
                <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-wc-black mb-8">
                  Your Partner in Financial Organization
                </h2>
                <div className="space-y-6 font-body text-base text-wc-muted leading-relaxed">
                  <p>
                    I am a Los Angeles native with a strong background in accounting, bookkeeping, and business operations management. As a graduate of <span className="text-wc-black font-medium">Loyola Marymount University</span>, where I studied Business Information Systems & Analytics, I combine a solid educational foundation with practical experience helping business owners make clearer, more confident financial decisions. I specialize in retail, real estate, nonprofit, and food and beverage accounting, and I have worked with organizations to bring structure, accuracy, and efficiency to their financial operations. Before founding William Covington & Associates, I built a reputation for improving disorganized accounting processes, strengthening reporting practices, and helping leadership better understand the financial side of their business.
                  </p>
                  <p>
                    At the heart of my work is the belief that business owners deserve more than basic bookkeeping. They deserve clarity, consistency, and a trusted professional who understands the responsibility that comes with leading a business. My work is guided by professionalism, service, and faith-informed values such as honesty, stewardship, accountability, and excellence. Through William Covington & Associates, I work directly with clients to provide professional bookkeeping, clear monthly reporting, and coordinated CPA support, helping business owners gain peace of mind, make better decisions, and keep their financial house in order.
                  </p>
                </div>
                <div className="w-12 h-px bg-wc-gold mt-10 mb-8" />
                <p className="font-body text-sm text-wc-muted italic leading-relaxed">
                  "Every business owner deserves to understand their numbers and feel confident in their financial foundation. That's what we deliver, month after month."
                </p>
                <p className="font-body text-sm font-semibold text-wc-black mt-3">— William K. Covington</p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* FIRM PHILOSOPHY */}
      <section className="py-24 lg:py-32 bg-wc-bg-alt" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <FadeIn>
            <div className="text-center mb-16">
              <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-4">Our Values</p>
              <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-wc-black">
                Built on Principle
              </h2>
            </div>
          </FadeIn>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-wc-border">
            {values.map((value, i) => (
              <FadeIn key={i} delay={i * 80}>
                <div className="bg-white p-10 h-full">
                  <value.icon className="w-6 h-6 text-wc-gold mb-5" strokeWidth={1.5} />
                  <h3 className="font-heading text-xl font-normal text-wc-black mb-3">{value.title}</h3>
                  <p className="font-body text-sm text-wc-muted leading-relaxed">{value.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST METRICS */}
      <section className="py-20 bg-wc-black" data-testid="about-metrics">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {metrics.map((metric, i) => (
              <FadeIn key={i} delay={i * 100}>
                <div className="text-center lg:text-left">
                  <p className="font-heading text-4xl sm:text-5xl font-light text-wc-gold">{metric.number}</p>
                  <p className="font-body text-sm text-white/50 mt-2 uppercase tracking-[0.1em]">{metric.label}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-white" data-testid="about-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <FadeIn>
            <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-6">Ready to Start?</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-wc-black mb-6">
              Let's Bring Order to Your Business
            </h2>
            <p className="font-body text-base text-wc-muted max-w-xl mx-auto mb-10 leading-relaxed">
              Schedule a consultation with William Covington and discover how professional bookkeeping can transform your business operations.
            </p>
            <button
              onClick={() => setOpen(true)}
              className="bg-wc-black text-white px-10 py-4 font-body text-sm uppercase tracking-[0.15em] hover:bg-wc-charcoal transition-colors duration-300"
              data-testid="about-cta-button"
            >
              Schedule a Consultation
            </button>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
